import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import {
  Dialog,
  Form,
  Input,
  InputNumber,
  MessagePlugin,
  Select,
  Switch,
  Textarea,
} from 'tdesign-react';
import { useMemoizedFn } from 'ahooks';
import formRulePattern from '@/utils/formRulePattern';
import { createNamespace, getDefaultNamespaceConfig } from '@/services/namespaceService';
import Text from '@/components/Text';
import Tip from '@/components/Tip';
import useDisableButton from '@/components/DisableWrapper/useDisableButton';
import { deal70101, isEmpty, isVoid, toTsOptions } from '@/utils/common';

const { FormItem } = Form;

function FormModal({ handleClose, visible, clusterName, tenantOptions, ...others }) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const confirmBtn = useDisableButton({ loading: submitting, content: '确定' });

  const doInit = useMemoizedFn(async () => {
    const { data } = await getDefaultNamespaceConfig(clusterName);
    form.setFieldsValue(data);
  });

  useEffect(() => {
    if (visible) {
      doInit();
    } else {
      form.reset();
    }
  }, [visible, form, doInit]);

  const doClose = () => handleClose();

  const handleSubmit = async ({ validateResult, fields }) => {
    if (validateResult === true) {
      // 消息过期大小 必须小于 保留消息大小
      const { backlogQuotaSize, retentionSizeInMB } = fields;
      if (!isVoid(backlogQuotaSize) && !isVoid(retentionSizeInMB)) {
        const [backlog, retention] = [Number(backlogQuotaSize), Number(retentionSizeInMB)]; // 保证类型一致
        // 只校验>0的情况
        if (backlog > 0 && retention > 0) {
          if (backlog >= retention) {
            MessagePlugin.warning('消息过期大小 必须小于 保留消息大小');
            return;
          }
        }
      }
      setSubmitting(true);
      try {
        const { meta } = await createNamespace(fields);
        // 主集群创建的资源已在灾备集群存在
        if (meta.code === 70101) {
          deal70101(meta.publicMessage, () => handleSubmit({ validateResult, fields }), {
            url: '/cluster/namespace/list',
            tenant: fields.tenant,
            namespace: fields.namespace,
          });
          return;
        }
        handleClose(true);
      } catch (error) {
        console.trace(error);
        throw error;
      } finally {
        setSubmitting(false);
      }
    }
  };

  return (
    <Root
      header='创建命名空间'
      onClose={doClose}
      onConfirm={() => form.submit()}
      confirmBtn={confirmBtn}
      visible={visible}
      width={660}
      placement='center'
      {...others}
    >
      <Form
        form={form}
        resetType='initial'
        onSubmit={handleSubmit}
        initialData={{ clusterName, messageTraceOpen: 0 }}
        labelWidth={160}
        style={{ width: '85%', paddingBottom: 15 }}
      >
        <FormItem name='clusterName' label='集群名称'>
          <Text />
        </FormItem>
        <FormItem name='tenant' label='租户' rules={[{ required: true }]}>
          <Select options={toTsOptions(tenantOptions)} />
        </FormItem>
        <FormItem
          name='namespace'
          label='命名空间'
          rules={[{ required: true }, formRulePattern('name')]}
        >
          <Input />
        </FormItem>

        <FormItem
          name='retentionTimeInSeconds'
          label={
            <Tip
              content={
                <>
                  <strong>保留消息时长</strong>
                  <li>1、被所有订阅者都消费并确认的消息保留时长。</li>
                  <li>2、如果存在消息未被确认，即使超过消息保留时长，也不会删除。</li>
                  <li>3、如果该topic没有一个订阅者，认为可删除超过的消息。</li>
                  <li>4、默认值为0，代表可立即标记删除，-1代表永不删除。</li>
                  <li>5、当前展示默认值为Borker配置默认值</li>
                </>
              }
            >
              保留消息时长
            </Tip>
          }
          rules={[
            { required: false },
            { validator: (val) => isEmpty(val) || val >= -1, message: '请输入>=-1的值' },
          ]}
        >
          <InputNumber suffix='秒' theme='normal' min={-1} />
        </FormItem>

        <FormItem
          name='retentionSizeInMB'
          label={
            <Tip
              content={
                <>
                  <strong>保留消息大小</strong>
                  <li>1、被所有订阅者都消费并确认的消息保留大小。</li>
                  <li>2、如果存在消息未被确认，即使超过保留消息大小，也不会删除。</li>
                  <li>3、如果该topic没有一个订阅者，认为可删除超过的消息。</li>
                  <li>4、默认值为0，代表可立即标记删除，-1代表永不删除。</li>
                  <li>5、当前展示默认值为Borker配置默认值</li>
                </>
              }
            >
              保留消息大小
            </Tip>
          }
          rules={[
            { required: false },
            { validator: (val) => isEmpty(val) || val >= -1, message: '请输入>=-1的值' },
          ]}
        >
          <InputNumber suffix='MB' theme='normal' min={-1} />
        </FormItem>

        <FormItem
          name='ttlInSeconds'
          label={
            <Tip
              content={
                <>
                  <strong>消息过期时长</strong>
                  <li>1、每条消息在每个订阅下的生命周期。</li>
                  <li>2、订阅者未消费的消息存活时长。</li>
                  <li>3、到达消息过期时长后，将自动确定未消费消息。</li>
                  <li>4、默认值为0，代表禁用。</li>
                  <li>5、主要使用场景，避免因长时间未消费导致数据积压无法清除。</li>
                  <li>6、当前展示默认值为Borker配置默认值</li>
                  <p></p>
                  <li>
                    例如配置为1小时，订阅者A能够消费到的数据为最近1小时，当然这取决于自动消息确定的速率。
                  </li>
                </>
              }
            >
              消息过期时长
            </Tip>
          }
          rules={[
            { required: false },
            { validator: (val) => isEmpty(val) || val >= 0, message: '请输入>=0的值' },
          ]}
        >
          <InputNumber suffix='秒' theme='normal' decimalPlaces={0} />
        </FormItem>
        <FormItem
          name='backlogQuotaSize'
          label={
            <Tip
              content={
                <>
                  <strong>消息过期大小</strong>
                  <li>1、每条消息在每个订阅下的生命周期。</li>
                  <li>2、订阅者未消费的消息大小。</li>
                  <li>3、到达消息过期大小后，将自动确定未消费消息。</li>
                  <li>4、默认值为-1，代表禁用。</li>
                  <li>5、主要使用场景，避免因未消费导致数据积压无法清除。</li>
                  <li>6、当前展示默认值为Borker配置默认值</li>
                  <p></p>
                  <li>
                    例如配置为1GB，订阅者A能够消费到的数据为最近1GB，当然这取决于自动消息确定的速率。
                  </li>
                </>
              }
            >
              消息过期大小
            </Tip>
          }
          rules={[
            { required: false },
            { validator: (val) => isEmpty(val) || val >= -1, message: '请输入>=-1的值' },
          ]}
        >
          <InputNumber suffix='MB' theme='normal' decimalPlaces={0} />
        </FormItem>

        <FormItem
          name='messageTraceOpen'
          label={
            <Tip content='请确保当前pulsar集群支持消息轨迹功能，否则该配置无法生效'>
              开启消息轨迹
            </Tip>
          }
        >
          <Switch customValue={[1, 0]} label={['是', '否']} />
        </FormItem>

        <FormItem name='app' label='应用'>
          <Input />
        </FormItem>
        <FormItem name='createUser' label='负责人'>
          <Input />
        </FormItem>
        {/* <FormItem name='label' label='标签'> */}
        {/*  <Input /> */}
        {/* </FormItem> */}
        <FormItem name='note' label='备注'>
          <Textarea placeholder='请输入' />
        </FormItem>
      </Form>
    </Root>
  );
}

export default connect((state) => ({
  tenantOptions: state.namespace.tenantOptions,
}))(FormModal);

const Root = styled(Dialog)`
  .t-input-number {
    width: auto;
    flex: 1;
  }
  .t-input__suffix {
    color: var(--td-font-gray-2);
  }
`;
